import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { getThread } from '../../apis';
import { agentErrorMessage, type GroupChatTurn } from './api';
import { mintOpId } from './opId';

const NO_TURNS: GroupChatTurn[] = [];

/**
 * The GROUP tab (#307): a chat with a group. A group is a router, never a
 * brain — the member whose objective binds the question answers in its own
 * name; a question no member binds (or two bind alike) is a roll-up, one
 * line per member. The transcript is the thread's own snapshot, like an
 * agent chat's (#202).
 */
export default function GroupTab({ threadId }: { threadId: string }) {
  const thread = useVoyagerStore((s) => s.savedThreads.find((t) => t.id === threadId));
  const turns = useVoyagerStore((s) => (s.savedThreads.find((t) => t.id === threadId)?.landingAgentTurns as GroupChatTurn[] | undefined) || NO_TURNS);
  const group = useVoyagerStore((s) => { const t = s.savedThreads.find((x) => x.id === threadId); return s.groups.find((g) => g.id === t?.sourceId) || null; });
  const fetchGroups = useVoyagerStore((s) => s.fetchGroups);
  const deliverGroupTurn = useVoyagerStore((s) => s.deliverGroupTurn);
  const navigateToAgentWall = useVoyagerStore((s) => s.navigateToAgentWall);
  const unsaved = useVoyagerStore((s) => !!s.agentChatUnsaved[threadId]);
  const groupId = thread?.sourceId || null;
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { if (!group) fetchGroups(); }, [group, fetchGroups]);
  // a promise made here is delivered into the thread by the server (#300/#307): while one is pending, the tab
  // re-reads the thread every ten seconds and takes the turns it does not have yet (a delivered promise is a
  // `promise_<id>` turn); nothing here is written back
  const pending = turns.some((t) => t.role === 'group' && t.promise?.id && !turns.some((d) => d.id === `promise_${t.promise?.id}`));
  useEffect(() => {
    if (!pending) return;
    let active = true;
    const refresh = async () => {
      try {
        const row = await getThread(threadId);
        if (!active) return;
        const server = (row?.snapshot as any)?.turns as GroupChatTurn[] | undefined;
        if (!Array.isArray(server)) return;
        const state = useVoyagerStore.getState();
        const current = (state.savedThreads.find((t) => t.id === threadId)?.landingAgentTurns as GroupChatTurn[] | undefined) || [];
        const have = new Set(current.map((t) => t.id));
        const fresh = server.filter((t) => t?.id && !have.has(t.id));
        if (fresh.length) useVoyagerStore.setState({ savedThreads: state.savedThreads.map((t) => (t.id === threadId ? { ...t, landingAgentTurns: [...current, ...fresh] } : t)) } as any);
      } catch { /* the next tick tries again */ }
    };
    refresh();
    const interval = window.setInterval(refresh, 10000);
    return () => { active = false; window.clearInterval(interval); };
  }, [pending, threadId]);
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [turns.length]);

  const deliver = async (turn: GroupChatTurn) => {
    if (!groupId) return;
    setError(null); setSending(true);
    try { await deliverGroupTurn(threadId, groupId, turn); }
    catch (e: any) { setError(agentErrorMessage(e, 'The group did not answer')); }
    finally { setSending(false); }
  };
  const send = () => {
    const clean = text.trim();
    if (!clean || sending || !groupId) return;
    setText('');
    const op = mintOpId();
    deliver({ id: op, role: 'user', text: clean, ts: new Date().toISOString(), op_id: op });
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const composing = (e.nativeEvent as any)?.isComposing || (e as any).isComposing;
    if ((e.key === 'Enter' || e.keyCode === 13) && !e.shiftKey && !composing) { e.preventDefault(); send(); }
  };

  if (!thread) return null;
  const name = group?.name || (thread.title || '').replace(/^@/, '') || 'Group';
  const address = group?.address || (thread.title || '').replace(/^@/, '');

  return (
    <Box data-testid="group-tab" data-group-id={groupId || ''} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      <Box data-testid="group-tab-bar" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0, flexWrap: 'wrap' }}>
        <Typography data-testid="group-tab-name" sx={{ fontSize: 13.5, fontWeight: 700 }}>@{address}</Typography>
        <Chip size="small" label={group?.kind === 'domain' ? 'BY DOMAIN' : group?.kind === 'preference' ? 'BY PREFERENCE' : 'GROUP'} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.5, bgcolor: 'var(--agent-tint)', color: 'var(--agent)' }} />
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          {name} · a router, never a brain — the member whose objective binds answers in its own name
        </Typography>
        <Box sx={{ flex: 1 }} />
        {(group?.members || []).map((m) => (
          <Link key={m.agent_id} component="button" type="button" underline="hover" onClick={() => navigateToAgentWall(m.agent_id)} data-testid="group-member" sx={{ fontSize: 12, font: 'inherit' }}>
            {m.agent_name}
          </Link>
        ))}
      </Box>

      <Box ref={listRef} data-testid="group-tab-turns" sx={{ flex: 1, overflow: 'auto', p: 3, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {turns.length === 0 && (
            <Typography data-testid="group-tab-empty" sx={{ fontSize: 13, color: 'text.disabled' }}>
              Ask @{address} anything a member owns — “@{address} why is Denver SLA off?” — and the member whose objective binds answers in its own name.
            </Typography>
          )}
          {turns.map((t) => (
            <Box key={t.id} data-testid="group-turn" data-role={t.role} data-spoken-as={t.spoken_as?.agent_name || ''}
              sx={{ alignSelf: t.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', p: 1.5, borderRadius: 2.5,
                    bgcolor: t.role === 'user' ? 'var(--accent-tint)' : 'background.paper', border: '1px solid', borderColor: t.failed ? 'var(--bad)' : 'divider' }}>
              {t.role === 'group' && (
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 0.5 }}>
                  {t.spoken_as
                    ? <Chip size="small" label={`${t.spoken_as.agent_name} · for @${address}`} data-testid="group-turn-speaker" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'var(--ok-tint)', color: 'var(--ok)' }} />
                    : t.id.startsWith('promise_')
                      ? <Chip size="small" label="PROMISE · delivered" data-testid="group-turn-delivered" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'var(--ok-tint)', color: 'var(--ok)' }} />
                    : <Chip size="small" label="ROLL-UP · one line per member" data-testid="group-turn-rollup" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'var(--agent-tint)', color: 'var(--agent)' }} />}
                </Box>
              )}
              {t.lines
                ? <Box component="ul" sx={{ m: 0, pl: 2 }}>{t.lines.map((l) => <li key={l.agent_id}><Typography sx={{ fontSize: 13.5 }}>{l.text}</Typography></li>)}</Box>
                : t.role === 'group' && (t.reply as any)?.redacted && !t.reply?.text && !t.text
                  ? <Typography variant="caption" color="text.secondary" data-testid="group-turn-redacted">
                      This answer is kept by reference — ask again to read it at your bar.
                    </Typography>
                  : <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{t.reply?.text ?? t.text}</Typography>}
              {t.failed && <Button size="small" onClick={() => deliver(t)} disabled={sending} data-testid="group-turn-retry">Retry</Button>}
            </Box>
          ))}
        </Box>
      </Box>

      <Box data-testid="group-composer" sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField fullWidth multiline maxRows={4} size="small" placeholder={`Ask @${address}…`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown}
          inputProps={{ 'data-testid': 'group-composer-input' }} disabled={!groupId} />
        <Button variant="contained" onClick={send} disabled={!text.trim() || sending || !groupId} data-testid="group-send">Send</Button>
      </Box>
      {(error || unsaved) && (
        <Typography data-testid="group-tab-error" sx={{ px: 3, py: 0.5, fontSize: 12, color: error ? 'var(--bad)' : 'text.secondary' }}>
          {error || 'Saving this chat…'}
        </Typography>
      )}
    </Box>
  );
}
